import React, { useEffect, useState, useCallback } from "react";
import { columnsHistory } from "../../../constants/paymentsPayout";
import BasicCustomTable from "../../../component/basicTable/index";
import { useSnackbar } from "../../../component/SnackbarContext";
import axiosInstance from "../../../configuration/request";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { formatDateDDMMYYYY } from "../../../utils/dateFormat";
import CustomPopupForm from "../../../component/customPopupModel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  extractListFromResponse,
  extractPaginationMeta,
  formatPayoutMoney,
  formatPayoutStatusLabel,
  isPayoutCompleteStatus,
  isPayoutFailedStatus,
  mapPartnerDetailsFromPayoutResponse,
} from "../../../utils/paymentPayoutHelpers";

function badgeClassForVariant(variant) {
  switch (variant) {
    case "success":
      return "bg-emerald-100/90 text-emerald-900 dark:bg-emerald-900/35 dark:text-emerald-100 border-emerald-200/70 dark:border-emerald-800/50";
    case "warning":
      return "bg-amber-100/90 text-amber-950 dark:bg-amber-900/40 dark:text-amber-100 border-amber-200/70 dark:border-amber-800/50";
    case "primary":
      return "bg-[#3383B2]/12 text-[#256f9c] dark:bg-[#3383B2]/22 dark:text-[#9cc7e0] border-[#3383B2]/30";
    default:
      return "bg-[var(--primary-bg-color)] text-[var(--light-text-color)] border-[var(--table-border)]";
  }
}

function PaymentAndHistory() {
  const showSnackbar = useSnackbar();
  const navigate = useNavigate();
  const [payouts, setPayouts] = useState([]);
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const storenameParam = searchParams.get("storename") || "";
  const decodedName = storenameParam
    ? decodeURIComponent(storenameParam)
    : "Partner";

  const { payoutsType: payoutsTypeRedux } = useSelector((state) => state.counter);
  const payoutsType =
    payoutsTypeRedux ||
    (typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem("adminPayoutType")
      : null) ||
    "store_payout";

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualRow, setManualRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [partnerDetails, setPartnerDetails] = useState(null);

  useEffect(() => {
    setPartnerDetails(null);
  }, [id, payoutsType]);

  const fetchPayouts = useCallback(
    (pageNumber, limit) => {
      if (!id) return;
      setLoading(true);
      const urlBase =
        payoutsType === "driver_payout"
          ? `/payment/payout?deliveryPersonId=${id}`
          : `/payment/payout?storeId=${id}`;

      axiosInstance
        .get(`${urlBase}&page=${pageNumber}&limit=${limit}`)
        .then((res) => {
          const body = res?.data;
          const rawUsers = extractListFromResponse(body);
          const { page: apiPage, total } = extractPaginationMeta(
            body,
            pageNumber,
            limit
          );

          setPartnerDetails(
            mapPartnerDetailsFromPayoutResponse(
              body,
              payoutsType,
              decodedName
            )
          );

          const formattedUser = rawUsers.map((item) => {
            const amountRaw =
              item?.amount ??
              item?.payment_amount ??
              item?.payout_amount ??
              item?.total;
            const statusRaw = item?.status ?? item?.payment_status ?? "";
            const paidAt = item?.paid_at ?? item?.paidAt ?? item?.created_at;
            const txn =
              item?.transaction_id ??
              item?.transactionId ??
              item?.stripe_transfer_id ??
              "—";

            return {
              paymentAmount: formatPayoutMoney(amountRaw),
              id: item?.id,
              storeName: item?.store?.storeName ?? "",
              totalOrderCount: item?.order_count ?? item?.orderCount ?? "—",
              trasactionId: txn,
              totalOrderValue: formatPayoutMoney(
                item?.total_order_value ?? item?.totalOrderValue
              ),
              paymentFlag: formatPayoutStatusLabel(statusRaw),
              isCompleted: isPayoutCompleteStatus(statusRaw),
              isFailed: isPayoutFailedStatus(statusRaw),
              paymentDate: paidAt ? formatDateDDMMYYYY(paidAt) : "—",
            };
          });

          setPayouts(formattedUser);
          setPage(apiPage || pageNumber);
          setTotalCount(total);
        })
        .catch((error) => {
          showSnackbar(
            error?.response?.data?.message || "Something went wrong!",
            "error"
          );
          setPayouts([]);
          setPartnerDetails(null);
        })
        .finally(() => setLoading(false));
    },
    [id, payoutsType, showSnackbar, decodedName]
  );

  useEffect(() => {
    if (id) fetchPayouts(1, rowsPerPage);
  }, [id, payoutsType, fetchPayouts, rowsPerPage]);

  const handlePageChangeRequest = (newPage, newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(newPage);
    fetchPayouts(newPage, newRowsPerPage);
  };

  const handlePay = (row) => {
    setSelectedRow(row);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
  };

  const handleMarkManualPaid = (row) => {
    if (!row?.isFailed) return;
    setManualRow(row);
    setManualOpen(true);
  };

  const handleCloseManual = () => {
    setManualOpen(false);
    setManualRow(null);
  };

  const partnerLoading = loading && partnerDetails === null;

  return (
    <div className="px-4 md:px-6 pb-10">
      <div className="flex flex-wrap items-center gap-3 pt-4 pb-2">
        <button
          type="button"
          onClick={() => navigate("/app/payment&payout")}
          className="inline-flex items-center gap-1 text-[var(--primary-color)] hover:underline text-sm font-medium"
        >
          <ArrowBackIcon fontSize="small" />
          Back to summary
        </button>
      </div>

      <h1 className="text-2xl font-semibold text-[var(--primary-text-color)] mt-1 mb-1">
        Payout history
      </h1>
      <p className="text-sm text-[var(--light-text-color)] mb-4">
        {payoutsType === "driver_payout" ? "Delivery partner" : "Store"} ·{" "}
        <span className="font-medium text-[var(--primary-text-color)]">
          {decodedName}
        </span>
      </p>

      <div className="mb-6 rounded-2xl border border-[var(--table-border)] bg-[var(--primary-bg-color)] shadow-sm overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-[#3383B2]/35 via-[#3383B2]/15 to-transparent" />
        <div className="px-4 py-4 md:px-5 md:py-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--light-text-color)]">
                {payoutsType === "driver_payout"
                  ? "Delivery partner"
                  : "Store partner"}
              </p>
              {partnerLoading ? (
                <p className="text-sm text-[var(--light-text-color)] mt-1">
                  Loading…
                </p>
              ) : partnerDetails?.title ? (
                <h2 className="text-lg md:text-xl font-semibold text-[var(--primary-text-color)] mt-0.5 truncate">
                  {partnerDetails.title}
                </h2>
              ) : null}
              <p className="text-xs text-[var(--light-text-color)] mt-1">
                ID{" "}
                <span className="font-mono text-[var(--primary-text-color)]">
                  {id}
                </span>
              </p>
            </div>
            {!partnerLoading &&
              partnerDetails?.kind === "store" &&
              partnerDetails.badges?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 justify-end">
                  {partnerDetails.badges.map((b) => (
                    <span
                      key={b.label}
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${badgeClassForVariant(
                        b.variant
                      )}`}
                    >
                      {b.label}
                    </span>
                  ))}
                </div>
              )}
            {!partnerLoading &&
              partnerDetails?.kind === "driver" && (
                <span
                  className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold shrink-0 ${badgeClassForVariant(
                    partnerDetails.active ? "success" : "neutral"
                  )}`}
                >
                  {partnerDetails.active ? "Active" : "Inactive"}
                </span>
              )}
          </div>

          {!partnerLoading && partnerDetails?.kind === "store" && (
            <div className="mt-4 pt-4 border-t border-[var(--table-border)]/70">
              {partnerDetails.owner && (
                <div className="mb-4 text-sm">
                  <p className="text-[var(--light-text-color)] text-xs font-medium mb-1">
                    Owner
                  </p>
                  <p className="font-semibold text-[var(--primary-text-color)]">
                    {partnerDetails.owner.name}
                  </p>
                  <p className="text-[var(--primary-text-color)] mt-1 break-all">
                    {partnerDetails.owner.email}
                    {partnerDetails.owner.phone
                      ? ` · ${partnerDetails.owner.phone}`
                      : ""}
                  </p>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-5">
                <div className="shrink-0">
                  {partnerDetails.thumbUrl ? (
                    <img
                      src={partnerDetails.thumbUrl}
                      alt=""
                      className="w-14 h-14 rounded-full object-cover border-2 border-[var(--table-border)] shadow-sm"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full border-2 border-dashed border-[var(--table-border)] bg-[var(--table-header-bg)]/50 flex items-center justify-center text-[10px] text-center text-[var(--light-text-color)] px-1 leading-tight">
                      No shop image
                    </div>
                  )}
                </div>
                {partnerDetails.overviewRows?.length > 0 ? (
                  <dl className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                    {partnerDetails.overviewRows.map(([label, value]) => (
                      <div key={label}>
                        <dt className="text-[var(--light-text-color)] text-xs font-medium">
                          {label}
                        </dt>
                        <dd className="text-[var(--primary-text-color)] font-medium break-words leading-snug mt-0.5">
                          {value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="text-xs text-[var(--light-text-color)]">
                    No overview fields returned.
                  </p>
                )}
              </div>
            </div>
          )}

          {!partnerLoading &&
            partnerDetails?.kind === "driver" &&
            partnerDetails.rows?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-[var(--table-border)]/70 flex flex-col sm:flex-row gap-4">
                <div className="w-14 h-14 rounded-full border-2 border-[var(--table-border)] overflow-hidden bg-[var(--table-header-bg)] flex items-center justify-center shrink-0 shadow-sm">
                  {partnerDetails.profileImageUrl ? (
                    <img
                      src={partnerDetails.profileImageUrl}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-lg font-semibold text-[var(--primary-color)]">
                      {(partnerDetails.title || "?").charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <dl className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                  {partnerDetails.rows.map(([label, value]) => (
                    <div key={label}>
                      <dt className="text-[var(--light-text-color)] text-xs font-medium">
                        {label}
                      </dt>
                      <dd className="text-[var(--primary-text-color)] font-medium break-words leading-snug mt-0.5">
                        {value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

          {!partnerLoading && !partnerDetails && (
            <p className="text-sm text-[var(--light-text-color)] mt-4 pt-4 border-t border-[var(--table-border)]/70">
              Partner details were not included in the response. History below
              is still for this partner.
            </p>
          )}
        </div>
      </div>

      <h2 className="text-lg font-semibold text-[var(--primary-text-color)] mb-2">
        History
      </h2>

      {loading && payouts.length === 0 ? (
        <div className="rounded-2xl border border-[var(--table-border)] bg-[var(--primary-bg-color)] py-16 text-center text-sm text-[var(--light-text-color)]">
          Loading payout history…
        </div>
      ) : (
        <BasicCustomTable
          columns={columnsHistory}
          rows={payouts}
          colorHeader={true}
          customClassHeader="table-header-cell-colored"
          tableName="Payment-history"
          maxHeightClass="max-h-[calc(100vh-460px)]"
          onPay={handlePay}
          onMarkManualPaid={handleMarkManualPaid}
          backendPagination={true}
          totalCount={totalCount}
          onPageChangeRequest={handlePageChangeRequest}
          pageLimit={rowsPerPage}
          paginationPageZeroBased={Math.max(0, page - 1)}
        />
      )}

      <CustomPopupForm
        isOpen={open}
        handleClose={handleClose}
        dialogType={"Payout"}
        dialogDetails={selectedRow}
        onSubmit={() => {
          handleClose();
          fetchPayouts(page, rowsPerPage);
        }}
      />

      <CustomPopupForm
        isOpen={manualOpen}
        handleClose={handleCloseManual}
        dialogType={"Manual-payout-status"}
        dialogDetails={manualRow}
        onSubmit={() => {
          handleCloseManual();
          fetchPayouts(page, rowsPerPage);
        }}
      />
    </div>
  );
}

export default PaymentAndHistory;
